import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Building2, Hash, BarChart3, FolderKanban, Settings, Briefcase, ListChecks,
  CalendarCheck, FileText, Clock, Download, Loader2, CalendarDays, Store,
} from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AdminAllReceipts from './AdminAllReceipts';
import UserManagement from './UserManagement';
import CostCenterManagement from './CostCenterManagement';
import GlCodeManagement from './GlCodeManagement';
import ProjectManagement from './ProjectManagement';
import ProjectEditPage from './ProjectEditPage';
import HolidayManagement from './HolidayManagement';
import TareasManagement from './TareasManagement';
import SageVendorManagement from './SageVendorManagement';
import AdminSettings from './AdminSettings';
import ErrorBoundary from '../shared/ErrorBoundary';
import VacacionesDashboardTab from '../shared/VacacionesDashboardTab';
import AuditLogViewer from './AuditLogViewer';
import SupervisorDetailPage from '../supervisor/SupervisorDetailPage';
import RoleSummaryTab from '../shared/RoleSummaryTab';
import PortalLayout from '../../shared/components/PortalLayout';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle, selectStyle } from '../../styles/tokens';
import client from '../../api/client';

/* ── Tablas sub-tabs ─────────────────────────────────────────────────── */

const TABLAS_TABS = [
  { key: 'users',        label: 'Usuarios',    icon: Users },
  { key: 'employees',    label: 'Empleados',   icon: Briefcase },
  { key: 'projects',     label: 'Proyectos',   icon: FolderKanban },
  { key: 'tareas',       label: 'Tareas',      icon: ListChecks },
  { key: 'cost-centers', label: 'Códigos',     icon: Building2 },
  { key: 'gl-codes',     label: 'Códigos GL',  icon: Hash },
  { key: 'sage-vendors', label: 'Proveedores Sage', icon: Store },
  { key: 'holidays',     label: 'Feriados',    icon: CalendarDays },
];

/* ── Empleados tab (adapted from PersonasPage) ─────────────────────── */

import PersonasPage from '../../timesheet/pages/PersonasPage';

/* ── Dashboard sub-tabs ────────────────────────────────────────────────── */

const DASH_TABS = [
  { key: 'gastos',     label: 'Gastos',      icon: FileText },
  { key: 'vacaciones', label: 'Vacaciones',  icon: CalendarCheck },
  { key: 'nomina',     label: 'Nómina',      icon: Clock },
];

/* ── Platform Dashboard (route: /admin) ──────────────────────────────── */

function PlatformDashboard() {
  const [tab, setTab] = useState('gastos');

  return (
    <>
      {/* Sub-tab bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: 0, padding: '0 20px',
        alignItems: 'stretch', overflowX: 'auto',
      }}>
        {DASH_TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '12px 20px', fontSize: 13,
            fontWeight: tab === key ? '700' : '500',
            color: tab === key ? AMBER : '#64748b',
            borderBottom: tab === key ? `2px solid ${AMBER}` : '2px solid transparent',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* V65 — cada pestaña va envuelta: una excepción de render desmonta hasta la raíz
          si nadie la atrapa, y así fue como AdminPtoStats dejó /admin en blanco entero. */}
      {tab === 'gastos' && <ErrorBoundary seccion="Dashboard de Gastos"><GastosDashboard /></ErrorBoundary>}
      {/* 🔴 V65 — LA MISMA pestaña que el dashboard general, no otra.
          `/admin` y `/dashboard` son DOS RUTAS distintas con barras propias, y el admin
          solo llega a la primera. Consolidar retirando la de acá lo dejó SIN NINGUNA —
          "una sola pestaña" es un solo COMPONENTE, no un solo punto de entrada. Se
          renderiza con `hideTabs` porque la barra la pone este portal. */}
      {tab === 'vacaciones' && (
        <ErrorBoundary seccion="Dashboard de Vacaciones">
          <VacacionesDashboardTab hideTabs />
        </ErrorBoundary>
      )}
      {tab === 'nomina' && <ErrorBoundary seccion="Dashboard de Nómina"><NominaDashboard /></ErrorBoundary>}
    </>
  );
}

/* ── Gastos Dashboard ─────────────────────────────────────────────────── */

function GastosDashboard() {
  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <AdminDashboard />
    </div>
  );
}

/* ⚰ V65 — VacacionesDashboard RETIRADA de acá.
   Era la SEGUNDA superficie de las mismas cifras de PTO, y eso no es una molestia
   estética: es lo que produjo el crash. `AdminPtoStats` quedó desalineado del contrato
   de V60 —cuatro claves— y nadie lo notó durante seis días porque vivía aparte, en una
   pestaña que solo el admin abría. Dos superficies divergen en silencio; una sola no
   puede. Mismo criterio con el que V62 retiró AdminPtoQueue.

   Dónde vive ahora: dashboard general → pestaña Vacaciones, en CAPAS por rol.
   `AdminPtoStats` se MOVIÓ entero (con su arreglo) como capa admin, y el calendario
   quedó adentro de esa misma pestaña. No se reimplementó nada. */

/* ── Nómina Dashboard — renders the same NominaHomePage all roles see ── */

function NominaDashboard() {
  return <RoleSummaryTab initialTab="nomina" hideTabs />;
}

/* ── Tablas Section (route: /admin/tablas) — has its own sub-tab bar ── */

function TablasSection() {
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    return location.state?.tab || 'users';
  });

  // Update tab when navigating back with state
  useEffect(() => {
    if (location.state?.tab) setTab(location.state.tab);
  }, [location.state?.tab]);

  return (
    <>
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: 0, padding: '0 20px',
        alignItems: 'stretch', overflowX: 'auto',
      }}>
        {TABLAS_TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '12px 16px', fontSize: 13,
            fontWeight: tab === key ? '700' : '500',
            color: tab === key ? AMBER : '#64748b',
            borderBottom: tab === key ? `2px solid ${AMBER}` : '2px solid transparent',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === 'users' && <UserManagement />}
      {tab === 'employees' && <PersonasPage />}
      {tab === 'projects' && <ProjectManagement />}
      {tab === 'tareas' && <TareasManagement />}
      {tab === 'cost-centers' && <CostCenterManagement />}
      {tab === 'gl-codes' && <GlCodeManagement />}
      {tab === 'sage-vendors' && <SageVendorManagement />}
      {tab === 'holidays' && <HolidayManagement />}
    </>
  );
}

/* ── Configuración Section (route: /admin/configuracion) ─────────────── */

function ConfiguracionSection() {
  return (
    <div>
      <AdminSettings />
    </div>
  );
}

/* ── Auditoría Section (route: /admin/auditoria) ──────────────────────── */

function AuditoriaSection() {
  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: '#1e293b', margin: '0 0 16px' }}>Auditoría</h2>
      <AuditLogViewer />
    </div>
  );
}

/* ── Main AdminPortal (Administración hub) ───────────────────────────── */

export default function AdminPortal() {
  const location = useLocation();

  let activeModule = 'dashboard';
  if (location.pathname.startsWith('/admin/tablas') || location.pathname.startsWith('/admin/configuracion') || location.pathname.startsWith('/admin/proyecto') || location.pathname.startsWith('/admin/auditoria')) activeModule = 'configuracion';
  else if (location.pathname.startsWith('/admin/recibos') || location.pathname.startsWith('/admin/receipt')) activeModule = 'gastos';

  return (
    <PortalLayout activeModule={activeModule}>
      <Routes>
        <Route index element={<PlatformDashboard />} />
        <Route path="tablas" element={<TablasSection />} />
        <Route path="configuracion" element={<ConfiguracionSection />} />
        <Route path="auditoria" element={<AuditoriaSection />} />
        <Route path="recibos" element={<AdminAllReceipts />} />
        <Route path="receipt/:id" element={<SupervisorDetailPage adminMode />} />
        <Route path="proyecto/:projectId" element={<ProjectEditPage />} />
      </Routes>
    </PortalLayout>
  );
}
